/**
 * JourneyStepper Component
 *
 * Displays journey progress steps with status indicators.
 */

import { CheckCircle2, Circle, Lock } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Link } from 'wouter';
import { cn } from '@tonnie/ui';

export type StepStatus = 'not-started' | 'in-progress' | 'completed';

export interface IJourneyStep {
  id: string;
  label: string;
  href: string;
  status: StepStatus;
  /** The journey's plan does not open this step (Care on a basic plan). */
  locked?: boolean;
}

interface IJourneyStepperProps {
  steps: IJourneyStep[];
  className?: string;
}

export function JourneyStepper({ steps, className }: IJourneyStepperProps) {
  const { t } = useTranslation();
  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center flex-1">
            <Link
              href={step.href}
              className="flex flex-col items-center flex-1 group cursor-pointer"
              data-testid={`journey-step-${step.id}`}
              data-locked={step.locked ? 'true' : undefined}
              aria-label={step.locked ? `${step.label} — ${t('common.locked')}` : undefined}
            >
              <div className="flex items-center w-full">
                {/* Step Circle */}
                <div className="relative flex flex-col items-center flex-1">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all',
                      step.status === 'completed'
                        ? 'bg-primary border-primary text-primary-foreground'
                        : step.status === 'in-progress'
                          ? 'bg-primary/20 border-primary text-primary-accessible'
                          : 'bg-muted border-border text-muted-foreground'
                    )}
                  >
                    {step.status === 'completed' ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : step.locked ? (
                      <Lock className="w-4 h-4" aria-hidden="true" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )}
                  </div>
                  {/* Step Label */}
                  <span
                    className={cn(
                      'mt-2 text-xs font-medium text-center transition-colors',
                      step.status === 'completed'
                        ? 'text-primary-accessible'
                        : step.status === 'in-progress'
                          ? 'text-primary-accessible'
                          : 'text-muted-foreground',
                      'group-hover:text-primary-accessible'
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 -mt-5">
                    <div
                      className={cn(
                        'h-full transition-colors',
                        step.status === 'completed' ? 'bg-primary' : 'bg-border'
                      )}
                    />
                  </div>
                )}
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
